import { useEffect, useRef, useState } from "react";
import {
  searchSuppliers,
  type SupplierMatch,
  type SupplierSearchResponse,
} from "../api";

const EXAMPLES = [
  "acme valve",
  "northpoint fab",
  "keystone labs",
  "solaris",
  "blue ridge",
];

function confidenceColor(c: number): string {
  if (c >= 0.85) return "bg-emerald-100 text-emerald-800";
  if (c >= 0.6) return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
}

function confidenceLabel(c: number): string {
  if (c >= 0.85) return "High confidence";
  if (c >= 0.6) return "Possible match";
  return "Low confidence";
}

export default function SupplierSearch() {
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState<SupplierSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    if (query.trim().length < 2) {
      setResponse(null);
      setError(null);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      runSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query]);

  async function runSearch(q: string) {
    setLoading(true);
    setError(null);
    try {
      const res = await searchSuppliers(q);
      setResponse(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Search failed");
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-slate-900">
          Smart supplier search
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Type a vendor name. Clotho pre-filters the supplier master with
          Postgres trigram similarity, then asks Claude to rank and score each
          candidate by the likelihood it's the same company — accounting for
          spelling, punctuation, suffixes (Inc, LLC, Co, Corp), and abbreviations.
        </p>

        <div className="mt-4">
          <label htmlFor="supplier-q" className="label">
            Vendor name
          </label>
          <input
            id="supplier-q"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. acme valve"
            className="input"
            autoFocus
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="text-xs text-slate-500">Try:</span>
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => setQuery(ex)}
                className="text-xs text-thread-600 hover:underline"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="card border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading && (
        <div className="card p-6 text-sm text-slate-500">Searching…</div>
      )}

      {response && response.matches.length === 0 && !loading && (
        <div className="card p-6 text-sm text-slate-600">
          No matches found for "{response.query}". In a real deployment, this
          would prompt the user to create a new supplier record via the
          supplier master.
        </div>
      )}

      {response && response.matches.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">
                {response.matches.length}
              </span>{" "}
              match{response.matches.length === 1 ? "" : "es"} for{" "}
              <span className="font-semibold text-slate-900">
                "{response.query}"
              </span>
            </div>
            <div className="text-xs text-slate-500">
              {response.elapsed_ms}ms
            </div>
          </div>

          <ul className="space-y-3">
            {response.matches.map((m: SupplierMatch) => (
              <li key={m.supplier_id} className="card p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-base font-semibold text-slate-900">
                        {m.display_name}
                      </h3>
                      <span
                        className={`pill ${confidenceColor(m.confidence)}`}
                      >
                        {Math.round(m.confidence * 100)}% ·{" "}
                        {confidenceLabel(m.confidence)}
                      </span>
                    </div>
                    {m.supplier_number && (
                      <div className="mt-1 text-xs text-slate-500">
                        Vendor number: {m.supplier_number}
                      </div>
                    )}
                    <p className="mt-2 text-sm text-slate-600">{m.reasoning}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="card bg-slate-50 p-4 text-xs text-slate-600">
            <strong className="font-semibold text-slate-800">
              Why this matters:
            </strong>{" "}
            Most procurement teams end up with 3–5 duplicate records for the
            same supplier because buyers can't find the existing one and create
            a new record instead. This fuzzy match + Claude rerank finds the
            existing record even when the buyer types a variant the system
            doesn't recognize by exact match.
          </div>
        </div>
      )}
    </div>
  );
}
